import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { logger } from '@/lib/logger';
import type { ChatProgressData } from '@/lib/reasoning/realtime/chatProgressPoll';
import { classifyChatProgress } from '@/lib/reasoning/realtime/chatProgressPoll';
import type {
  ChainFailedEvent,
  StepProgressEvent,
} from '@/lib/reasoning/realtime/types';
import { useChatProgress } from '@/lib/reasoning/realtime/useChatProgress';

import type {
  PartialStepsPayload,
  ReasoningStepSummary,
} from './latestStepSummary';
import {
  extractLatestStepSummary,
  isSameStepSummary,
} from './latestStepSummary';

interface UseProposalChatProgressOptions {
  onComplete: () => void;
  onError: (error: Error) => void;
}

type ProgressData = ChatProgressData & PartialStepsPayload;

/**
 * WebSocket-first progress hook for the mounted action panel. Same
 * *parameter* signature as `useProposalPolling`, for drop-in use at the
 * panel's call site — driven internally by `useChatProgress` (WS with a
 * polling fallback) instead of pure polling. The return shape is
 * intentionally smaller: only `{ latestStep }`, since the caller doesn't
 * need `isPolling`/`progressDescription`.
 */
export function useProposalChatProgress(
  taskId: string | null,
  conversationId: string | null,
  orgId: string | undefined,
  options: UseProposalChatProgressOptions
) {
  const { onComplete, onError } = options;
  const [latestStep, setLatestStep] = useState<ReasoningStepSummary | null>(
    null
  );

  const stableOnComplete = useEffectEvent(onComplete);
  const stableOnError = useEffectEvent(onError);

  const previousTaskIdRef = useRef<string | null>(null);
  const wsFailedForTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Clear only when a NEW non-null task starts. When taskId goes null (a
    // tracked turn completed and clearPolling() reset it), keep the final
    // reasoning step visible instead of blanking it on the completion
    // transition — the panel still shows the last content until the next turn.
    if (taskId && taskId !== previousTaskIdRef.current) {
      setLatestStep(null);
      wsFailedForTaskIdRef.current = null;
    }
    previousTaskIdRef.current = taskId;
  }, [taskId]);

  const setLatestStepDeduped = useCallback((next: ReasoningStepSummary) => {
    setLatestStep(prev => (isSameStepSummary(prev, next) ? prev : next));
  }, []);

  const onStepProgress = useCallback(
    (event: StepProgressEvent) => {
      setLatestStepDeduped({
        stage: event.step.stage,
        content: event.step.content,
        step_number: event.step.step_number,
        is_final: event.step.is_final,
      });
    },
    [setLatestStepDeduped]
  );

  // This hook does not dedupe completion signals — every WS `chain.completed`
  // and every poll-reported completion forwards to onComplete as-is. The
  // caller's tracker (chatTaskTracker's synchronous `resolved` guard) owns
  // exactly-once semantics; see ProposalDetailPanel.tsx:327-331.
  const onChainCompleted = useCallback(() => {
    logger.debug('[useProposalChatProgress] terminal via ws', {
      component: 'proposals',
      source: 'ws',
    });
    stableOnComplete();
    // stableOnComplete is an Effect Event (useEffectEvent) — stable by design,
    // must NOT be in the dep array (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChainFailed = useCallback(
    (event: ChainFailedEvent) => {
      logger.debug('[useProposalChatProgress] failure via ws', {
        component: 'proposals',
        source: 'ws',
      });
      wsFailedForTaskIdRef.current = taskId;
      stableOnError(
        new Error(
          event.error || 'Something went wrong processing your message.'
        )
      );
    },
    // stableOnError is an Effect Event (useEffectEvent) — stable by design,
    // must NOT be in the dep array (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskId]
  );

  const onTitleUpdated = useCallback(() => {}, []);

  const onPollingUpdate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polling data structure varies by task type
    (data: any) => {
      const progress = data as ProgressData;
      const next = extractLatestStepSummary(progress);
      if (next) {
        setLatestStepDeduped(next);
      }

      // A proposal turn surfaces as `paused` when done and awaiting review.
      const outcome = classifyChatProgress(progress, {
        treatPausedAsComplete: true,
      });
      if (outcome === 'failed') {
        logger.debug('[useProposalChatProgress] failure via poll', {
          component: 'proposals',
          source: 'poll',
        });
        stableOnError(
          new Error('Something went wrong processing your message.')
        );
      } else if (outcome === 'complete') {
        // Forwarded as-is, no dedup against a prior WS completion — see the
        // comment on onChainCompleted above; the caller's tracker is what
        // guarantees exactly-once handling. The wsFailedForTaskIdRef check
        // below is a different guard: it covers a prior WS *failure* (not
        // completion) for this same task, which must never be silently
        // overridden by a later poll-based 'complete' read. It's scoped to
        // the current taskId so a late failure event for a superseded task
        // can't poison the next task's completion.
        if (wsFailedForTaskIdRef.current === taskId) {
          logger.warn(
            '[useProposalChatProgress] suppressed poll-complete after observed ws failure',
            {
              component: 'proposals',
              source: 'poll',
              taskId,
            }
          );
        } else {
          logger.debug('[useProposalChatProgress] terminal via poll', {
            component: 'proposals',
            source: 'poll',
          });
          stableOnComplete();
        }
      }
    },
    // stableOnComplete and stableOnError are Effect Events (useEffectEvent) —
    // stable by design, must NOT be in the dep array (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLatestStepDeduped, taskId]
  );

  const onPollingError = useCallback(
    (message: string) => {
      logger.warn('[useProposalChatProgress] polling error', {
        component: 'proposals',
        source: 'poll',
        taskId,
      });
      stableOnError(new Error(message));
    },
    // stableOnError is an Effect Event (useEffectEvent) — stable by design,
    // must NOT be in the dep array (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskId]
  );

  useChatProgress({
    orgId,
    conversationId,
    taskId,
    onStepProgress,
    onChainCompleted,
    onChainFailed,
    onTitleUpdated,
    onPollingUpdate,
    onPollingError,
    enabled: !!conversationId,
  });

  return { latestStep };
}
